'use client';

import { useState, useRef, useEffect } from 'react';

interface CategoryComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  onAddNew?: (category: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  addNewText?: string;
  noMatchText?: string;
}

export function CategoryCombobox({
  value,
  onChange,
  options,
  onAddNew,
  label,
  placeholder = 'Select or type category...',
  required = false,
  disabled = false,
  addNewText = 'Add "{value}" as new category',
  noMatchText = 'No matching categories',
}: CategoryComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const filtered = options.filter((opt) =>
      opt.toLowerCase().includes(inputValue.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [inputValue, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // If the input value doesn't match any option and is not empty, keep it as custom
        if (inputValue && !options.includes(inputValue)) {
          onChange(inputValue);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputValue, options, onChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
  };

  const handleSelectOption = (option: string) => {
    setInputValue(option);
    onChange(option);
    setIsOpen(false);
  };

  const handleAddNew = () => {
    if (inputValue.trim() && !options.includes(inputValue.trim())) {
      const newCategory = inputValue.trim();
      if (onAddNew) {
        onAddNew(newCategory);
      }
      onChange(newCategory);
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        handleSelectOption(filteredOptions[0]);
      } else if (inputValue.trim()) {
        handleAddNew();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const showAddNew = inputValue.trim() && !options.some(
    (opt) => opt.toLowerCase() === inputValue.toLowerCase()
  );

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => !disabled && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={disabled}
          className={`w-full px-3 py-2 rounded-lg bg-card-bg border border-card-border text-foreground pr-10 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
        <button
          type="button"
          onClick={() => {
            if (disabled) return;
            setIsOpen(!isOpen);
            inputRef.current?.focus();
          }}
          disabled={disabled}
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-background-secondary transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <svg
            className={`w-4 h-4 text-foreground-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-card-bg border border-card-border rounded-lg shadow-lg max-h-60 overflow-auto">
          {filteredOptions.length > 0 ? (
            <ul className="py-1">
              {filteredOptions.map((option) => (
                <li key={option}>
                  <button
                    type="button"
                    onClick={() => handleSelectOption(option)}
                    className={`w-full px-3 py-2 text-left hover:bg-primary/10 transition-colors ${
                      option === value ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
                    }`}
                  >
                    {option}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-3 py-2 text-sm text-foreground-muted">
              {noMatchText}
            </div>
          )}
          
          {showAddNew && (
            <div className="border-t border-card-border">
              <button
                type="button"
                onClick={handleAddNew}
                className="w-full px-3 py-2 text-left hover:bg-success/10 transition-colors flex items-center gap-2 text-success"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>{addNewText.replace('{value}', inputValue.trim())}</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

